import React from 'react';
import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { ITitle } from '@interfaces/common/common';

import { columnSizes } from '@constants/table';

import { renderSeverityCell, renderTitleOrNameCell } from './columnUtils';

interface IOranCombinedColumnsProps {
	openEditAlarmForm: (alarm: IAlarmManagement) => void;
}

const columnHelper = createColumnHelper<IAlarmManagement>();

export const getOranCombinedColumns = ({ openEditAlarmForm }: IOranCombinedColumnsProps): ColumnDef<any>[] => {
	return ([
		columnHelper.accessor('alarmId', {
			header: 'alarm id',
			size: columnSizes.size115,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		columnHelper.accessor('name', {
			header: 'alarm name',
			size: columnSizes.size215,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		{
			header: 'vendor',
			accessorKey: 'vendor',
			size: columnSizes.size115,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		},
		columnHelper.accessor('group', {
			header: 'group',
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		columnHelper.accessor('moduleName', {
			header: 'module',
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		columnHelper.accessor('roles', {
			header: 'roles',
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => {
				const value = getValue();
				const roles: string[] | undefined = value?.map(({ title }: ITitle) => title);
				const rolesTooltip = roles?.length
					? <div>{roles.map(title => <div key={title}>{title}</div>)}</div>
					: undefined;

				return (
					<AdaptiveTableCell
						text={roles?.join(', ')}
						tooltipText={rolesTooltip}
					/>
				);
			},
		}),
		columnHelper.accessor('users', {
			header: 'users',
			size: columnSizes.size155,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => {
				const value = getValue();
				const users: string[] | undefined = value?.map((v: { fullName: string }) => v?.fullName);
				const usersTooltip = users?.length
					? <div>{users.map((fullName, index) => <div key={`${fullName}${index}`}>{fullName}</div>)}</div>
					: undefined;

				return (
					<AdaptiveTableCell
						text={users?.join(', ')}
						tooltipText={usersTooltip}
					/>
				);
			},
		}),
		columnHelper.accessor('status', {
			header: 'status',
			size: columnSizes.size115,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => {
				const value = getValue();
				const title = typeof value === 'object' && value?.title
					? value.title
					: value
						? 'Enable'
						: 'Disable';

				return <AdaptiveTableCell text={title} />;
			},
		}),
		columnHelper.accessor('severity', {
			header: 'severity',
			size: columnSizes.size85,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderSeverityCell(getValue),
		}),
		columnHelper.accessor('applicability', {
			header: 'applicability',
			size: columnSizes.size215,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		columnHelper.accessor('probableCause', {
			header: 'probable cause',
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		columnHelper.accessor('notes', {
			header: 'notes',
			size: columnSizes.size335,
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAlarmManagement, any>) => renderTitleOrNameCell(getValue),
		}),
		columnHelper.display({
			id: 'actions',
			header: 'actions',
			size: columnSizes.size80,
			enableSorting: false,
			cell: ({ row }: CellContext<IAlarmManagement, any>) => {
				const handleEdit = () => openEditAlarmForm(row?.original);

				return (
					<Actions
						editHandler={handleEdit}
						editTooltip='Edit details'
						gridNumberOfColumns={1}
						isEdit
					/>
				);
			},
		}),
	]);
};
